import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GoogleAuthProvider,
  signInWithPopup,
  getIdToken,
  getRedirectResult,
  signInWithRedirect
} from 'firebase/auth';
import { auth } from '../Firebase';

const AuthGoogle = () => {
  const navigate = useNavigate();
  const { register: authRegister } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isRedirect, setIsRedirect] = useState(false);

  useEffect(() => {
    // Check for redirect result on component mount
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('Redirect result received:', result);
          await handleAuthResult(result);
        }
      } catch (error) {
        console.error('Redirect result error:', error);
        setErrors({ auth: error.message });
      }
    };

    checkRedirectResult();
  }, []);

  const handleAuthResult = async (result) => {
    setLoading(true);
    setErrors({});

    try {
      const user = result.user;
      console.log('Google authentication successful:', user.uid);

      // Get Firebase ID token
      const idToken = await getIdToken(user);
      console.log('Google ID token obtained');

      // Extract user info
      let firstName = '';
      let lastName = '';
      let email = user.email || '';

      // Parse display name
      if (user.displayName) {
        const nameParts = user.displayName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      // Additional info from token response
      if (result._tokenResponse) {
        const tokenResponse = result._tokenResponse;
        if (tokenResponse.firstName) {
          firstName = tokenResponse.firstName;
          lastName = tokenResponse.lastName || '';
        }
      }

      // Register/Login with backend
      const userData = await authRegister({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase().trim(),
        provider: 'google',
        idToken: idToken
      });

      console.log('Google registration/login successful:', userData);

      // Navigate to profile
      navigate('/profile');

    } catch (error) {
      console.error('Google authentication result error:', error);
      setErrors({ auth: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setErrors({});

    try {
      console.log('Starting Google authentication...');

      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      if (isRedirect) {
        // Use redirect method
        await signInWithRedirect(auth, provider);
      } else {
        // Use popup method
        const result = await signInWithPopup(auth, provider);
        await handleAuthResult(result);
      }

    } catch (error) {
      console.error('Google authentication error:', error);
      setErrors({ auth: error.message });
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: '400px',
        width: '100%'
      }}>
        <h2>{t('auth.login')} {t('auth.or')} Google</h2>
        <p>{t('auth.continueWith')} Google {t('auth.or')} {t('auth.register')}</p>

        <button
          onClick={handleGoogleAuth}
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px 20px',
            margin: '20px 0',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            backgroundColor: '#4285f4',
            color: 'white',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#3367d6';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#4285f4';
            }
          }}
        >
          {loading ? (
            <>{t('common.loading')}</>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.continueWith')} Google
            </>
          )}
        </button>

        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={isRedirect}
              onChange={(e) => setIsRedirect(e.target.checked)}
            />
            Utiliser la redirection (pour mobile)
          </label>
        </div>

        {errors.auth && (
          <div style={{
            color: '#e74c3c',
            fontSize: '14px',
            marginTop: '10px',
            padding: '8px',
            backgroundColor: '#fdf2f2',
            borderRadius: '4px',
            border: '1px solid #f5c6cb'
          }}>
            {errors.auth}
          </div>
        )}

        <div style={{
          marginTop: '20px',
          fontSize: '12px',
          color: '#666'
        }}>
          En continuant, vous acceptez nos{' '}
          <a href="/privacy-policy" style={{ color: '#4285f4', textDecoration: 'none' }}>
            Conditions d'utilisation
          </a>
        </div>
      </div>
    </div>
  );
};

export default AuthGoogle;
